import { useQueryClient } from "@tanstack/react-query";
import { useSelector } from "react-redux";

import useActionAfterFlagChange from "app/pages/sales-intelligence/hooks/useActionAfterFlagChange";
import type { LeadsType } from "app/pages/sales-intelligence/modules/leads-search";
import { selectActiveLeadsType } from "app/pages/sales-intelligence/modules/leads-search";
import { useListParams } from "app/pages/sales-intelligence/modules/lists/context/ListParamsContext";
import { opportunityContactsQueryKeys } from "app/pages/sales-intelligence/pages/opportunity-list/query/queryKeyFactory";
import { useIsContactsDataAvailable } from "app/pages/sales-intelligence/sub-modules/contacts-search/hooks/useContactsDataAvailability";
import {
    selectContactSearchResultsDtoWithAggregation,
    selectQuotaIsBeingFetched,
} from "app/pages/sales-intelligence/sub-modules/contacts-search/store/selectors";

import useOpportunityContactsSearchResultsQuery from "../query/queries/useOpportunityContactsSearchResultsQuery";

type Params = {
    throwOnError?: boolean | ((error: unknown) => boolean);
};

export const useOpportunityListContactsResults = ({ throwOnError }: Params = {}) => {
    const queryClient = useQueryClient();

    const listParams = useListParams();

    const activeLeadsType: LeadsType = useSelector(selectActiveLeadsType);
    const dto = useSelector(selectContactSearchResultsDtoWithAggregation);
    const isQuotaBeingFetched = useSelector(selectQuotaIsBeingFetched);

    const isContactsDataAvailable = useIsContactsDataAvailable();

    useActionAfterFlagChange(isQuotaBeingFetched, () => {
        void queryClient.invalidateQueries({
            queryKey: opportunityContactsQueryKeys.tableData(listParams.id),
        });
    });

    return useOpportunityContactsSearchResultsQuery({
        listParams,
        leadsType: activeLeadsType,
        dto,
        enabled: isContactsDataAvailable,
        throwOnError,
    });
};

export const useInvalidateOpportunityListContactsResults = (): VoidFunction => {
    const queryClient = useQueryClient();

    const listParams = useListParams();

    return () => {
        void queryClient.invalidateQueries({
            queryKey: opportunityContactsQueryKeys.tableData(listParams.id),
        });
    };
};
